import React, { useState } from 'react';
import { API_BASE } from '../../apiBase';
import axios from 'axios';

const ProductSync: React.FC = () => {
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleSync = async (clearFirst = false) => {
        const confirmMsg = clearFirst
            ? 'This will WIPE your entire Pinecone index and then sync ONLY ACTIVE products from Shopify. Continue?'
            : 'This will fetch all ACTIVE products from Shopify and upload them to Pinecone. Continue?';

        if (!window.confirm(confirmMsg)) return;

        setSyncLoading(true);
        setSyncMessage({ type: 'success', text: (clearFirst ? 'Clearing and ' : '') + 'Sync started... This can take 5-10 minutes. Please do not close this tab.' });

        try {
            const response = await axios.post(
                `${API_BASE}/api/products/sync-pinecone`,
                { clearFirst },
                { withCredentials: true, timeout: 600000 }
            );

            if (response.data.success) {
                const { processedProducts, successfulVectors, errors } = response.data.data;
                setSyncMessage({
                    type: 'success',
                    text: `✅ ${clearFirst ? 'Clean ' : ''}Sync Complete! Processed ${processedProducts} products (${successfulVectors} variants). Errors: ${errors}`
                });
            } else {
                setSyncMessage({ type: 'error', text: 'Sync failed: ' + response.data.error });
            }
        } catch (error: any) {
            setSyncMessage({
                type: 'error',
                text: 'Error syncing: ' + (error.response?.data?.error || error.message || 'Timeout or Network Error.')
            });
        } finally {
            setSyncLoading(false);
        }
    };

    const handleClearIndex = async () => {
        if (!window.confirm('⚠️ WARNING: This will PERMANENTLY DELETE all product vectors. Are you sure?')) return;

        setSyncLoading(true);
        setSyncMessage({ type: 'success', text: 'Clearing index... please wait.' });

        try {
            const response = await axios.post(
                `${API_BASE}/api/products/sync-pinecone`,
                { action: 'clear' },
                { withCredentials: true }
            );

            if (response.data.success) {
                setSyncMessage({ type: 'success', text: '✅ Database cleared successfully!' });
            } else {
                setSyncMessage({ type: 'error', text: 'Clear failed: ' + response.data.error });
            }
        } catch (error: any) {
            setSyncMessage({ type: 'error', text: 'Error clearing: ' + (error.response?.data?.error || error.message) });
        } finally {
            setSyncLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 sm:p-8 space-y-8">
            <div className="text-center space-y-4">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-fuchsia-600 to-pink-600 text-transparent bg-clip-text">
                    Product Database Sync 🔄
                </h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                    Keep your Pinecone vector database in sync with your Shopify active products.
                    This enables AI-powered recommendations and visual search.
                </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold mb-2">Sync Controls</h3>
                        <p className="text-sm text-slate-600 mb-4">
                            Upload your <strong>ACTIVE</strong> Shopify products. Draft and archived products are excluded.
                        </p>

                        {syncMessage && (
                            <div className={`p-4 rounded-lg mb-6 text-sm flex items-start gap-3 ${syncMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                                <span className="text-lg">{syncMessage.type === 'success' ? '✅' : '❌'}</span>
                                <div>{syncMessage.text}</div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button
                                onClick={() => handleSync(false)}
                                disabled={syncLoading}
                                className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-fuchsia-100 to-pink-100 border border-pink-200 rounded-xl hover:from-fuchsia-200 hover:to-pink-200 transition-all group disabled:opacity-50"
                            >
                                <span className="text-3xl mb-2 group-hover:scale-110 transition-transform">🚀</span>
                                <span className="font-bold text-pink-700">Quick Sync</span>
                                <span className="text-xs text-pink-600/70 mt-1">Incremental update</span>
                            </button>

                            <button
                                onClick={() => handleSync(true)}
                                disabled={syncLoading}
                                className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-indigo-100 to-blue-100 border border-indigo-200 rounded-xl hover:from-indigo-200 hover:to-blue-200 transition-all group disabled:opacity-50"
                            >
                                <span className="text-3xl mb-2 group-hover:scale-110 transition-transform">✨</span>
                                <span className="font-bold text-indigo-700">Clean Re-sync</span>
                                <span className="text-xs text-indigo-600/70 mt-1">Wipe and full refresh</span>
                            </button>
                        </div>
                    </div>

                    <div className="pt-6 border-t border-slate-100">
                        <div className="flex justify-between items-center bg-slate-50 p-4 rounded-lg">
                            <div className="text-sm text-slate-600">
                                <span className="font-semibold text-slate-800">Dangerous Action:</span> Clear the entire database if you need a fresh start.
                            </div>
                            <button
                                onClick={handleClearIndex}
                                disabled={syncLoading}
                                className="px-4 py-2 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-50"
                            >
                                🗑️ Clear Database
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                    <span className="text-xl">💡</span> Tip: Data Integrity
                </h4>
                <p className="text-sm text-blue-700 leading-relaxed">
                    If you have recently moved products to "Draft" status in Shopify, use the <strong>Clean Re-sync</strong> option to ensure your vector database stays perfectly aligned with your active catalog.
                </p>
            </div>
        </div>
    );
};

export default ProductSync;
